// The option vocabulary for a filterable column.
//
// Most columns carry theirs statically in `columns.ts`. The per-district id
// columns do not: `treatment.product`, `treatment.intervention` and the two
// creator columns hold the district's own product, intervention and user ids,
// whose members and names live in the stores. The trap and lab vocabulary
// columns are the same kind of column — a species, a trap type, a lure, a test
// target or method, the pool's sex, a test's status — and read the district's
// live vocabularies. Anything that renders one of those values has to resolve it
// here, or it shows the raw id — a filter chip reading "Product is 5" instead of
// "Product is Dibrom".
//
// It sits apart from `columns.ts` on purpose: that module is the static
// vocabulary and imports no store, so it stays usable from tests and from the
// server-side registry export.
import { computedAsync } from "@vueuse/core";

import { useStoreIntervention } from "@/store/intervention";
import { useStoreMapReview } from "@/store/map-review";
import { useStoreProduct } from "@/store/product";
import { useUserStore } from "@/store/user";
import type {
	MapReviewTrapVocabulary,
	MapReviewVocabularyEntry,
} from "@/type/api";
import type { MapReviewColumn, MapReviewColumnOption } from "./columns";

/** The store-backed sources whose vocabulary the server filters by FACET rather
 * than by row id: the pool's sex (`sex.facet`) and a test's status
 * (`test_status.facet`). What the operator picks is therefore the facet, and
 * what they read is the district's own name for the row that carries it. */
const FACET_OPTION_SOURCES: Record<string, true> = {
	sex: true,
	testStatus: true,
};

/** The live vocabulary one store-backed source reads. The four product /
 * intervention / user sources are not vocabularies and answer with nothing —
 * their callers never reach here. */
function vocabularyFor(
	source: string,
	vocab: MapReviewTrapVocabulary,
): MapReviewVocabularyEntry[] {
	switch (source) {
		case "species":
			return vocab.species;
		case "sex":
			return vocab.sex;
		case "trapType":
			return vocab.trap_type;
		case "lure":
			return vocab.lure;
		case "testTarget":
			return vocab.test_target;
		case "testMethod":
			return vocab.test_method;
		case "testStatus":
			return vocab.test_status;
		default:
			return [];
	}
}

/** One vocabulary's members as filter options. A facet-backed source offers each
 * facet once, under the district's name for it, with the name-less rows left
 * out: a row carrying no facet is not a member of a facet vocabulary, and it is
 * the facet the clause matches. */
function vocabularyOptions(
	source: string,
	vocab: MapReviewTrapVocabulary,
): MapReviewColumnOption[] {
	const list = vocabularyFor(source, vocab);
	if (!FACET_OPTION_SOURCES[source]) {
		return list.map((entry) => ({ value: entry.id, label: entry.name }));
	}
	const options: MapReviewColumnOption[] = [];
	const seen = new Set<string>();
	for (const entry of list) {
		const facet = entry.facet;
		if (facet === null || seen.has(facet)) continue;
		seen.add(facet);
		options.push({ value: facet, label: entry.name });
	}
	return options;
}

export function useColumnOptions() {
	const storeProduct = useStoreProduct();
	const storeIntervention = useStoreIntervention();
	const storeUser = useUserStore();
	const storeMapReview = useStoreMapReview();

	// The district's users, fetched once per component that needs them. Sorted
	// by display name because the panel is a plain checkbox list; inactive
	// members are kept, since the historical members the imports create for
	// unmatched source names are exactly the ones already recorded on the rows.
	// A member with no rows simply matches nothing, which is correct.
	const userOptions = computedAsync(async (): Promise<
		MapReviewColumnOption[]
	> => {
		const users = await storeUser.withAll().catch(() => []);
		return users
			.map((u) => ({ value: u.id, label: u.display_name }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, []);

	function optionsFor(column: MapReviewColumn): MapReviewColumnOption[] {
		if (column.optionsSource === "product") {
			return storeProduct.products.map((p) => ({ value: p.id, label: p.name }));
		}
		if (column.optionsSource === "intervention") {
			return storeIntervention.interventions.map((i) => ({
				value: i.id,
				label: i.name,
			}));
		}
		if (column.optionsSource === "user") return userOptions.value;
		// The trap and lab vocabularies come from the district's own tables, so
		// the options are fetched once and read wherever the vocabulary columns
		// and the counts clause's controls need them.
		if (column.optionsSource) {
			const vocab = storeMapReview.vocabulary;
			return vocab ? vocabularyOptions(column.optionsSource, vocab) : [];
		}
		return column.options ?? [];
	}

	// The vocabularies are one request per session; the filter controls ask for
	// them when they first render, and the call is idempotent.
	void storeMapReview.loadVocabulary();

	return { optionsFor };
}
